import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useState } from 'react';

import { HOMEPAGE_REVAMP } from '@/utils/featureFlags';

import { resolveFlagFromQueryAndCookie } from './useFeature';

const HomepageRevampContext = createContext(false);

type HomepageRevampProviderProps = {
  /** Server-resolved flag from page props; undefined on static pages such as 404. */
  enabled: boolean | undefined;
  children: React.ReactNode;
};

/**
 * Shares the server-resolved `homepage-revamp` flag with the homepage and the
 * header. Static pages carry no value and keep the last known one, so the
 * navigation does not flip when moving through a 404.
 */
export function HomepageRevampProvider({ enabled, children }: HomepageRevampProviderProps) {
  const [lastKnown, setLastKnown] = useState(enabled ?? false);
  if (enabled !== undefined && enabled !== lastKnown) setLastKnown(enabled);

  // Persist a query override as a cookie so the proxy keeps the variant on later requests.
  const { asPath } = useRouter();
  useEffect(() => {
    resolveFlagFromQueryAndCookie(HOMEPAGE_REVAMP);
  }, [asPath]);

  return (
    <HomepageRevampContext.Provider value={enabled ?? lastKnown}>
      {children}
    </HomepageRevampContext.Provider>
  );
}

export const useHomepageRevamp = () => useContext(HomepageRevampContext);
